import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import AdminButton from '../../../components/admin/AdminButton';
import { getFedexShipments, cancelFedexShipment } from '../../../services/fedexApi';

const FedexShipmentsList = () => {
  const navigate = useNavigate();
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  // Fonction pour récupérer les expéditions FedEx via notre service
  const fetchFedexShipments = async () => {
    setLoading(true);
    setError(null);

    try {
      // Appel au service pour récupérer les expéditions FedEx
      const response = await getFedexShipments({
        page,
        perPage
      });
      
      // Vérifier si la réponse contient des données
      if (response && response.shipments) {
        setShipments(response.shipments);
      } else {
        // Si l'API ne renvoie pas de données, utiliser des données de démonstration
        // Cela permet de continuer à développer même si l'API n'est pas encore disponible
        const mockShipments = [
          {
            id: '1',
            tracking_number: '794644915445',
            created_at: '2023-08-01T10:30:00Z',
            status: 'created',
            service_type: 'PRIORITY_OVERNIGHT',
            recipient_name: 'Jean Dupont',
            recipient_address: '123 Rue de Paris, 75001 Paris, France',
            estimated_delivery: '2023-08-02T18:00:00Z'
          },
          {
            id: '2',
            tracking_number: '794644915446',
            created_at: '2023-08-02T14:15:00Z',
            status: 'in_transit',
            service_type: 'FEDEX_EXPRESS_SAVER',
            recipient_name: 'Marie Martin',
            recipient_address: '456 Avenue des Champs-Élysées, 75008 Paris, France',
            estimated_delivery: '2023-08-05T18:00:00Z'
          },
          {
            id: '3',
            tracking_number: '794644915447',
            created_at: '2023-08-03T09:45:00Z',
            status: 'delivered',
            service_type: 'FEDEX_GROUND',
            recipient_name: 'Pierre Durand',
            recipient_address: '789 Boulevard Haussmann, 75009 Paris, France',
            estimated_delivery: '2023-08-08T18:00:00Z',
            delivered_at: '2023-08-08T14:30:00Z'
          },
          {
            id: '4',
            tracking_number: '794644915448',
            created_at: '2023-08-04T16:20:00Z',
            status: 'cancelled',
            service_type: 'INTERNATIONAL_PRIORITY',
            recipient_name: 'Sophie Lefebvre',
            recipient_address: '101 Rue de Rivoli, 75001 Paris, France',
            estimated_delivery: '2023-08-06T18:00:00Z',
            cancelled_at: '2023-08-05T10:15:00Z'
          }
        ];
        setShipments(mockShipments);
        console.warn('Utilisation de données de démonstration car l\'API FedEx n\'a pas renvoyé de données');
      }
    } catch (err) {
      console.error('Erreur lors de la récupération des expéditions FedEx:', err);
      setError('Impossible de récupérer les expéditions FedEx. Veuillez réessayer plus tard.');
      
      // En cas d'erreur, utiliser des données de démonstration pour permettre le développement
      const mockShipments = [
        {
          id: '1',
          tracking_number: '794644915445',
          created_at: '2023-08-01T10:30:00Z',
          status: 'created',
          service_type: 'PRIORITY_OVERNIGHT',
          recipient_name: 'Jean Dupont',
          recipient_address: '123 Rue de Paris, 75001 Paris, France',
          estimated_delivery: '2023-08-02T18:00:00Z'
        },
        {
          id: '2',
          tracking_number: '794644915446',
          created_at: '2023-08-02T14:15:00Z',
          status: 'in_transit',
          service_type: 'FEDEX_EXPRESS_SAVER',
          recipient_name: 'Marie Martin',
          recipient_address: '456 Avenue des Champs-Élysées, 75008 Paris, France',
          estimated_delivery: '2023-08-05T18:00:00Z'
        }
      ];
      setShipments(mockShipments);
      toast.error('Erreur lors de la récupération des expéditions FedEx. Données de démonstration affichées.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFedexShipments();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // Fonction pour formater le statut de l'expédition
  const formatStatus = (status) => {
    switch (status) {
      case 'delivered':
        return { label: 'Livré', class: 'bg-green-100 text-green-800' };
      case 'in_transit':
        return { label: 'En transit', class: 'bg-blue-100 text-blue-800' };
      case 'pending':
        return { label: 'En attente', class: 'bg-yellow-100 text-yellow-800' };
      case 'cancelled':
        return { label: 'Annulé', class: 'bg-red-100 text-red-800' };
      default:
        return { label: 'Inconnu', class: 'bg-gray-100 text-gray-800' };
    }
  };

  // Fonction pour annuler une expédition FedEx
  const handleCancelShipment = async (trackingNumber) => {
    // Demander confirmation avant d'annuler
    if (!window.confirm(`Êtes-vous sûr de vouloir annuler l'expédition ${trackingNumber} ? Cette action est irréversible.`)) {
      return;
    }
    
    try {
      setLoading(true);
      
      // Appel au service pour annuler l'expédition
      await cancelFedexShipment(trackingNumber);
      
      toast.success(`Expédition ${trackingNumber} annulée avec succès`);
      
      // Mise à jour de l'état local pour refléter l'annulation
      setShipments(prevShipments => 
        prevShipments.map(shipment => 
          shipment.tracking_number === trackingNumber 
            ? { ...shipment, status: 'cancelled', cancelled_at: new Date().toISOString() }
            : shipment
        )
      );
    } catch (error) {
      console.error('Erreur lors de l&apos;annulation de l&apos;expédition:', error);
      toast.error('Impossible d&apos;annuler l&apos;expédition. Veuillez réessayer plus tard.');
      
      // En mode développement, simuler une annulation réussie pour les tests
      if (process.env.NODE_ENV === 'development') {
        console.warn('Mode développement: simulation d\'une annulation réussie');
        
        // Mise à jour de l'état local pour simuler l'annulation
        setShipments(prevShipments => 
          prevShipments.map(shipment => 
            shipment.tracking_number === trackingNumber 
              ? { ...shipment, status: 'cancelled', cancelled_at: new Date().toISOString() }
              : shipment
          )
        );
        
        toast.success('(DEV) Expédition FedEx annulée avec succès (simulation)');
      }
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour afficher les détails d'une expédition
  const handleViewShipmentDetails = (trackingNumber) => {
    navigate(`/admin/fedex-shipments/${trackingNumber}`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-secondary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <span className="text-red-600 font-semibold">{error}</span>
      </div>
    );
  }

  return (
    <div>
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Expéditions FedEx</h2>
          <Link to="/admin/fedex-shipments/new" className="btn-modern btn-primary-modern">
            Nouvelle expédition FedEx
          </Link>
        </div>
        <div className="flex gap-2">
          <AdminButton 
            type="secondary" 
            icon="refresh"
            onClick={fetchFedexShipments}
          >
            Actualiser
          </AdminButton>
        </div>
      </div>

      {shipments.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-6 text-center">
          <p className="text-gray-500">Aucune expédition FedEx trouvée.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    N° de suivi
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Destinataire
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Service
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date d&apos;envoi
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {shipments.map((shipment) => {
                  const statusInfo = formatStatus(shipment.status);
                  return (
                    <tr key={shipment.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {shipment.trackingNumber || shipment.tracking_number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {shipment.recipient?.name || shipment.recipientName}<br />
                        <span className="text-xs">{shipment.recipient?.address || shipment.recipientAddress}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {shipment.service || (shipment.shippingDetails && shipment.shippingDetails.service)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusInfo.class}`}>
                          {statusInfo.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(shipment.created_at || shipment.createdAt).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleViewShipmentDetails(shipment.tracking_number || shipment.trackingNumber)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Détails
                          </button>
                          {shipment.status !== 'cancelled' && shipment.status !== 'delivered' && (
                            <>
                              <button
                                onClick={() => navigate(`/admin/fedex-shipments/edit/${shipment.tracking_number || shipment.trackingNumber}`)}
                                className="text-green-600 hover:text-green-900"
                              >
                                Modifier
                              </button>
                              <button
                                onClick={() => handleCancelShipment(shipment.tracking_number || shipment.trackingNumber)}
                                className="text-red-600 hover:text-red-900"
                              >
                                Annuler
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setPage(page > 1 ? page - 1 : 1)}
                disabled={page === 1}
                className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                  page === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Précédent
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={shipments.length < perPage}
                className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                  shipments.length < perPage ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Suivant
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Affichage de <span className="font-medium">{shipments.length > 0 ? (page - 1) * perPage + 1 : 0}</span> à{' '}
                  <span className="font-medium">{Math.min(page * perPage, shipments.length)}</span> sur{' '}
                  <span className="font-medium">{shipments.length}</span> résultats
                </p>
              </div>
              <div>
                <select
                  value={perPage}
                  onChange={(e) => {
                    setPerPage(Number(e.target.value));
                    setPage(1);
                  }}
                  className="mr-2 border-gray-300 rounded-md shadow-sm focus:ring-secondary focus:border-secondary sm:text-sm"
                >
                  <option value={5}>5 par page</option>
                  <option value={10}>10 par page</option>
                  <option value={20}>20 par page</option>
                  <option value={50}>50 par page</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FedexShipmentsList;
